import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

const selectStyles = cva(
  'block w-full appearance-none rounded-md border bg-bg-canvas text-fg-primary transition-base ease-token focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 disabled:bg-bg-subtle disabled:cursor-not-allowed pr-10',
  {
    variants: {
      size: {
        sm: 'h-8 pl-2 text-sm',
        md: 'h-10 pl-3 text-base',
        lg: 'h-12 pl-4 text-lg',
      },
      tone: {
        default: 'border-border-subtle hover:border-border-strong',
        error: 'border-danger focus-visible:ring-danger',
      },
    },
    defaultVariants: { size: 'md', tone: 'default' },
  },
);

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>, VariantProps<typeof selectStyles> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: readonly SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, size, tone, label, hint, error, options, placeholder, id, ...props },
  ref,
) {
  const reactId = useId();
  const selectId = id ?? reactId;
  const describedById = hint || error ? `${selectId}-desc` : undefined;
  const resolvedTone = error ? 'error' : tone;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={selectId} className="text-fg-primary text-sm font-medium">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          className={cn(selectStyles({ size, tone: resolvedTone }), className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          className="text-fg-secondary pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2"
          aria-hidden="true"
        >
          <path d="m5 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {(hint || error) && (
        <p id={describedById} className={cn('text-xs', error ? 'text-danger' : 'text-fg-muted')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
